// Pure decision logic for bus station-discovery convergence, extracted from useStationDiscovery so it can
// be unit-tested without React/react-query. A bus seller answers a city search non-deterministically —
// sometimes the station list, sometimes a non-bookable "fare menu" the backend marks `unavailable`. The
// client re-broadcasts a bounded number of times to converge on the station list before surfacing a real
// "no operators". Metro never retries — its `unavailable` is terminal.

export const MAX_BUS_DISCOVERY_RETRIES = 3;

export type VehicleCategory = 'METRO' | 'BUS';

export interface DiscoveryFlags {
  /** A bus discovery that is `unavailable` but still has retry budget — stay in "discovering", not terminal. */
  busRetrying: boolean;
  /** The honest terminal "no operators" state the empty-state UI shows. */
  unavailable: boolean;
}

/**
 * deriveDiscoveryFlags computes the terminal-vs-still-trying flags for a discovery result. For BUS, an
 * `unavailable` stays non-terminal (`busRetrying`) while retry budget remains; for METRO it is immediately
 * terminal (`unavailable`).
 */
export function deriveDiscoveryFlags(params: {
  vehicleCategory: VehicleCategory;
  rawUnavailable: boolean;
  busRetries: number;
  max?: number;
}): DiscoveryFlags {
  const { vehicleCategory, rawUnavailable, busRetries, max = MAX_BUS_DISCOVERY_RETRIES } = params;
  const busRetrying = vehicleCategory === 'BUS' && rawUnavailable && busRetries < max;
  return { busRetrying, unavailable: rawUnavailable && !busRetrying };
}

/**
 * shouldRebroadcastBus decides whether to fire another bounded bus discovery re-broadcast. It fires only
 * for BUS, only when the result is `unavailable` with zero stations, only while under the retry budget, and
 * at most ONCE per distinct discovery transaction (guarded by lastRetriedTxn) so it never loops within a
 * single draft. Metro never re-broadcasts.
 */
export function shouldRebroadcastBus(params: {
  vehicleCategory: VehicleCategory;
  rawUnavailable: boolean;
  stationsLen: number;
  busRetries: number;
  txnId: string | null;
  lastRetriedTxn: string | null;
  max?: number;
}): boolean {
  const {
    vehicleCategory,
    rawUnavailable,
    stationsLen,
    busRetries,
    txnId,
    lastRetriedTxn,
    max = MAX_BUS_DISCOVERY_RETRIES,
  } = params;
  return (
    vehicleCategory === 'BUS' &&
    rawUnavailable &&
    stationsLen === 0 &&
    busRetries < max &&
    Boolean(txnId) &&
    lastRetriedTxn !== txnId
  );
}
